import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarMonthIcon,
  Collections as CollectionsIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useParams } from "react-router-dom";
import api from "../api/api";

function normalizeBoolean(value, fallback = true) {
  if ([true, 1, "1", "true"].includes(value)) {
    return true;
  }

  if ([false, 0, "0", "false"].includes(value)) {
    return false;
  }

  return fallback;
}

function normalizeAlbum(rawAlbum) {
  return {
    id: rawAlbum?.AlbumId || rawAlbum?.albumId || rawAlbum?.id,
    title: rawAlbum?.AlbumTitle || rawAlbum?.albumTitle || "",
    description: rawAlbum?.AlbumDescription || rawAlbum?.albumDescription || "",
    eventDate: (rawAlbum?.EventDate || rawAlbum?.eventDate || "").slice(0, 10),
    coverImage:
      rawAlbum?.CoverImage ||
      rawAlbum?.coverImage ||
      rawAlbum?.CoverImageUrl ||
      rawAlbum?.coverImageUrl ||
      "",
    isActive: normalizeBoolean(rawAlbum?.IsActive ?? rawAlbum?.isActive, true),
    imagesCount: Number(rawAlbum?.ImagesCount || rawAlbum?.imagesCount || 0),
  };
}

function normalizeImage(rawImage) {
  return {
    id:
      rawImage?.GalleryImageId || rawImage?.galleryImageId || rawImage?.ImageId,
    title: rawImage?.ImageTitle || rawImage?.imageTitle || "",
    description: rawImage?.ImageDescription || rawImage?.imageDescription || "",
    imageUrl:
      rawImage?.ImageUrl ||
      rawImage?.imageUrl ||
      rawImage?.ImagePath ||
      rawImage?.imagePath ||
      rawImage?.FilePath ||
      rawImage?.filePath ||
      rawImage?.Url ||
      rawImage?.url ||
      "",
    displayOrder: Number(rawImage?.DisplayOrder ?? rawImage?.displayOrder ?? 0),
    isActive: normalizeBoolean(rawImage?.IsActive ?? rawImage?.isActive, true),
  };
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function extractAlbums(payload) {
  return [
    ...toArray(payload?.albums),
    ...toArray(payload?.data?.albums),
    ...toArray(payload?.galleryAlbums),
    ...toArray(payload?.data?.galleryAlbums),
    ...(Array.isArray(payload) ? payload : []),
  ];
}

function extractAlbum(payload) {
  return (
    payload?.album ||
    payload?.data?.album ||
    payload?.galleryAlbum ||
    payload?.data?.galleryAlbum ||
    payload?.data ||
    payload
  );
}

function extractImages(payload) {
  return [
    ...toArray(payload?.images),
    ...toArray(payload?.data?.images),
    ...toArray(payload?.galleryImages),
    ...toArray(payload?.data?.galleryImages),
    ...toArray(payload?.album?.images),
    ...toArray(payload?.album?.galleryImages),
    ...toArray(payload?.album?.GalleryImages),
    ...toArray(payload?.data?.album?.images),
    ...toArray(payload?.data?.album?.galleryImages),
    ...toArray(payload?.galleryAlbum?.images),
    ...toArray(payload?.galleryAlbum?.galleryImages),
  ];
}

function getErrorMessage(error, fallbackMessage) {
  const data = error?.response?.data;

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data?.message) {
    return data.message;
  }

  return error?.message || fallbackMessage;
}

function formatDate(dateString) {
  if (!dateString) {
    return "Date not specified";
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "Date not specified";
  }

  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function GalleryEmptyState({ title, description }) {
  return (
    <Box
      sx={{
        py: 8,
        px: 3,
        borderRadius: 4,
        textAlign: "center",
        border: "1px dashed rgba(15, 23, 42, 0.16)",
        bgcolor: "rgba(248, 250, 252, 0.9)",
      }}
    >
      <CollectionsIcon sx={{ fontSize: 40, color: "#64748b", mb: 1.5 }} />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography color="text.secondary">{description}</Typography>
    </Box>
  );
}

function GalleryAlbumList() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadAlbums() {
      try {
        setLoading(true);
        setError("");
        const response = await api.getGalleryAlbums({ IsActive: 1 });
        const responseData = response?.data || response || {};
        const albumList = extractAlbums(responseData);

        if (active) {
          setAlbums(albumList.map(normalizeAlbum).filter((album) => album.isActive));
        }
      } catch (loadError) {
        if (active) {
          setError(getErrorMessage(loadError, "Unable to load gallery albums."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadAlbums();

    return () => {
      active = false;
    };
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 7 } }}>
      <Stack spacing={4}>
        <Box
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 5,
            color: "white",
            background:
              "linear-gradient(135deg, rgba(3, 105, 161, 0.96) 0%, rgba(15, 23, 42, 0.96) 100%)",
          }}
        >
          <Chip
            label="Public Gallery"
            sx={{
              mb: 2,
              color: "white",
              bgcolor: "rgba(255,255,255,0.14)",
              fontWeight: 700,
            }}
          />
          <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5 }}>
            Community albums, open to everyone
          </Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: 720, color: "rgba(255,255,255,0.84)" }}
          >
            Browse event albums without signing in. Open any album to view all
            published images.
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && !error && albums.length === 0 ? (
          <GalleryEmptyState
            title="No albums available"
            description="Published albums will appear here once images are added."
          />
        ) : null}

        {!loading && albums.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 3,
            }}
          >
            {albums.map((album) => (
              <Card
                key={album.id}
                sx={{
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
                }}
              >
                <CardActionArea component={RouterLink} to={`/gallery/${album.id}`}>
                  <Box
                    sx={{
                      position: "relative",
                      height: 260,
                      bgcolor: "#e2e8f0",
                    }}
                  >
                    {album.coverImage ? (
                      <Box
                        component="img"
                        src={album.coverImage}
                        alt={album.title}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            "linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)",
                        }}
                      >
                        <PhotoLibraryIcon sx={{ fontSize: 56, color: "#2563eb" }} />
                      </Box>
                    )}
                    <Chip
                      label={`${album.imagesCount} images`}
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        bgcolor: "rgba(15, 23, 42, 0.72)",
                        color: "white",
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={1.25}>
                      <Typography variant="h6" fontWeight={800} color="#0f172a">
                        {album.title || "Untitled album"}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center", color: "text.secondary" }}
                      >
                        <CalendarMonthIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2">
                          {formatDate(album.eventDate)}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          minHeight: 40,
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {album.description || "Open this album to view the photos."}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        ) : null}
      </Stack>
    </Container>
  );
}

function GalleryAlbumDetail() {
  const { albumId } = useParams();
  const [album, setAlbum] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadAlbum() {
      try {
        setLoading(true);
        setError("");
        const response = await api.getGalleryAlbumById(albumId, { IsActive: 1 });
        const responseData = response?.data || response || {};
        const resolvedAlbum = normalizeAlbum(extractAlbum(responseData));
        const resolvedImages = extractImages(responseData).map(normalizeImage);

        if (active) {
          setAlbum(resolvedAlbum?.id ? resolvedAlbum : null);
          setImages(
            resolvedImages
              .filter((image) => image.isActive && image.imageUrl)
              .sort((first, second) => first.displayOrder - second.displayOrder),
          );
        }
      } catch (loadError) {
        if (active) {
          setError(getErrorMessage(loadError, "Unable to load this album."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadAlbum();

    return () => {
      active = false;
    };
  }, [albumId]);

  const imageCountLabel = useMemo(() => {
    const count = images.length;
    return `${count} image${count === 1 ? "" : "s"}`;
  }, [images]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 7 } }}>
      <Stack spacing={4}>
        <Box>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/gallery"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: "none", px: 0, minWidth: 0 }}
            >
              Gallery
            </Button>
            <Typography color="text.primary">
              {album?.title || "Album"}
            </Typography>
          </Breadcrumbs>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && !error && !album ? (
          <GalleryEmptyState
            title="Album not found"
            description="This album is unavailable or has no public content."
          />
        ) : null}

        {!loading && album ? (
          <>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 5,
                background:
                  "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(224, 242, 254, 0.98) 100%)",
                border: "1px solid rgba(15, 23, 42, 0.08)",
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="h3" fontWeight={800} color="#0f172a">
                  {album.title || "Untitled album"}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip icon={<CollectionsIcon />} label={imageCountLabel} />
                  <Chip
                    icon={<CalendarMonthIcon />}
                    label={formatDate(album.eventDate)}
                  />
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  {album.description || "Published images from the community gallery."}
                </Typography>
              </Stack>
            </Box>

            {images.length === 0 ? (
              <GalleryEmptyState
                title="No images in this album"
                description="Images will appear here once they are published."
              />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 3,
                }}
              >
                {images.map((image, index) => (
                  <Card
                    key={image.id || `${image.imageUrl}-${index}`}
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: "0 16px 34px rgba(15, 23, 42, 0.08)",
                    }}
                  >
                    <Box
                      component="img"
                      src={image.imageUrl}
                      alt={image.title || `${album.title} image ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: 320,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    {image.title || image.description ? (
                      <CardContent sx={{ p: 2.25 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {image.title || `Image ${index + 1}`}
                        </Typography>
                        {image.description ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {image.description}
                          </Typography>
                        ) : null}
                      </CardContent>
                    ) : null}
                  </Card>
                ))}
              </Box>
            )}
          </>
        ) : null}
      </Stack>
    </Container>
  );
}

function Gallery() {
  const { albumId } = useParams();

  if (albumId) {
    return <GalleryAlbumDetail />;
  }

  return <GalleryAlbumList />;
}

export default Gallery;
